import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
    errorInfo: React.ErrorInfo | null;
}

/**
 * Global Error Boundary — catches React render errors and shows a recovery UI
 * instead of a blank/black screen.
 */
export class ErrorBoundary extends React.Component<
    { children: React.ReactNode },
    ErrorBoundaryState
> {
    constructor(props: { children: React.ReactNode }) {
        super(props);
        this.state = { hasError: false, error: null, errorInfo: null };
    }

    static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
        console.error('[ErrorBoundary] Caught render error:', error, errorInfo);
        this.setState({ errorInfo });
    }

    render() {
        if (this.state.hasError) {
            return (
                <div style={{
                    minHeight: '100vh',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    background: '#0a0e1a',
                    padding: '2rem',
                }}>
                    <div style={{
                        maxWidth: '500px',
                        width: '100%',
                        background: 'rgba(255,255,255,0.04)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: '16px',
                        padding: '2rem',
                        color: '#e2e8f0',
                        fontFamily: 'system-ui, -apple-system, sans-serif',
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                            <AlertTriangle size={24} color="#f59e0b" />
                            <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600 }}>Something went wrong</h2>
                        </div>
                        <p style={{ color: '#94a3b8', fontSize: '14px', marginBottom: '16px' }}>
                            A rendering error occurred. This is usually temporary.
                        </p>
                        <div style={{
                            background: 'rgba(239,68,68,0.1)',
                            border: '1px solid rgba(239,68,68,0.2)',
                            borderRadius: '8px',
                            padding: '12px',
                            fontSize: '12px',
                            color: '#f87171',
                            fontFamily: 'monospace',
                            overflowX: 'auto',
                            marginBottom: '16px',
                            maxHeight: '150px',
                            overflowY: 'auto',
                        }}>
                            {this.state.error?.message || 'Unknown error'}
                        </div>
                        <button
                            onClick={() => window.location.reload()}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                                padding: '10px 20px',
                                background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
                                border: 'none',
                                borderRadius: '8px',
                                color: 'white',
                                fontSize: '14px',
                                fontWeight: 500,
                                cursor: 'pointer',
                                width: '100%',
                                justifyContent: 'center',
                            }}
                        >
                            <RefreshCw size={14} />
                            Reload Page
                        </button>
                    </div>
                </div>
            );
        }

        return this.props.children;
    }
}

export default ErrorBoundary;
